import { FaBars } from '@react-icons/all-files/fa/FaBars';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';
import { useTranslation } from 'next-i18next';
import React, { useRef, useState } from 'react';
import { Button } from '@/components/atoms/Button/Button';
import { Drawer } from '@/components/atoms/Drawer/Drawer';
import { Ul } from '@/components/atoms/List/List';
import { Li } from '@/components/atoms/Typography/Typography';
import { Contact, ContactEntry } from '@/components/molecules/Contact/Contact';
import { PageLocaleSelect } from '@/containers/PageLayout/components/PageLocaleSelect';
import { PageSocialLinks } from '@/containers/PageLayout/components/PageSocialLinks/PageSocialLinks';
import { NavbarLinkForSideBar } from '@/containers/PageLayout/components/SideNavbar/NavbarLinkForSideBar/NavbarLinkForSideBar';
import { pushEvent } from '@/lib/gtm/gtmEvents';
import { useData } from '@/lib/pageData/pageDataContext';
import styles from './SideNavbar.module.scss';

export interface SideNavbarProps {
  disableContact?: boolean;
}

export const SideNavbarComponent = ({ disableContact }: SideNavbarProps) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { navLinks, contact } = useData<'default'>();

  const setOpen = () => setIsOpen(true);
  const setClose = () => setIsOpen(false);

  const links = [
    navLinks.bikes,
    navLinks.ebikes,
    navLinks.testRide,
    navLinks.service,
    // TODO: delete this link permanently after Q2 2024
    navLinks.jobsDE,
    navLinks.stores,
    navLinks.help,
    navLinks.account,
  ].filter(link => link);

  return (
    <>
      <Button
        onClick={() => {
          pushEvent({ name: 'Menu open' });
          setOpen();
        }}
        variant="icon"
        color="default"
        aria-label={t('drawer.hamburger')}
        data-test-id="sidemenu-hamburger"
        className={styles.menuButton}
      >
        <FaBars size="35px" />
      </Button>
      <Drawer
        isOpen={isOpen}
        onDismiss={setClose}
        className={styles.container}
        ariaLabel={t('drawer.menu')}
        data-test-id="sidemenu"
      >
        <Button
          onClick={() => {
            pushEvent({ name: 'Menu close' });
            setClose();
          }}
          variant="icon"
          color="default"
          className={styles.cross}
          aria-label={t('drawer.close')}
          ref={buttonRef}
          data-test-id="sidemenu-close"
        >
          <FaTimes size="30px" />
        </Button>
        <div className={styles.links}>
          <Ul className={styles.navigation} data-test-id="sideNavbar-navLinks">
            {links.map(link => (
              <Li
                key={link.key}
                onClick={() =>
                  pushEvent({
                    name: 'Menu item click',
                    details: link.url,
                  })
                }
                className={styles.navLink}
              >
                <NavbarLinkForSideBar onClick={setClose} link={link} />
              </Li>
            ))}
          </Ul>
          <div className={styles.bottomControls}>
            {!disableContact && contact ? (
              <Contact title={t('contact.title')} className={styles.contact}>
                <ContactEntry
                  label={t('contact.call')}
                  url={`tel:${contact.call.replace(/\s+/g, '')}`}
                  urlLabel={contact.call}
                  onClick={setClose}
                />
                <ContactEntry
                  label={t('contact.whatsapp')}
                  url={`tel:${contact.whatsapp.replace(/\\D/g, '')}}`}
                  urlLabel={contact.whatsapp}
                  onClick={setClose}
                />
                <ContactEntry
                  label={t('contact.email')}
                  url={contact.contactUrl}
                  urlLabel={t('contact.urlLabel')}
                  onClick={setClose}
                />
              </Contact>
            ) : null}
            <PageLocaleSelect
              portal={false}
              className={styles.localeSelect}
              onSelect={({ isCountryChanged, isLanguageChanged }) => {
                isCountryChanged && pushEvent({ name: 'Menu change country' });
                isLanguageChanged &&
                  pushEvent({ name: 'Menu change language' });
                setClose();
              }}
            />
            <PageSocialLinks onClick={setClose} className={styles.social} />
          </div>
        </div>
      </Drawer>
    </>
  );
};
